// v50.14 — ShellNotificationToaster.
// Mounted inside ProtectedShell. Subscribes to placement_notifications
// for the current user and fires a toast on every new row. Users
// working in other surfaces (pack assembly, tender list, etc.) get an
// immediate signal instead of having to glance at the bell.
//
// Skips rows older than the component mount time so we never spam
// historical events on first paint, and never toasts for events the
// user already saw (read_at populated).

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/api/supabase-client";
import { useToast } from "@/components/Toast";
import { useShellUser } from "@/features/auth/AuthContext";
import type { NotificationKind } from "@/lib/api/types";

interface NewNotificationRow {
  id:           string;
  user_id:      string;
  kind:         NotificationKind | string;
  payload?:     Record<string, unknown> | null;
  placement_id?: string | null;
  read_at?:     string | null;
  created_at:   string;
}

export function ShellNotificationToaster() {
  const user     = useShellUser();
  const toast    = useToast();
  const navigate = useNavigate();
  /* Record component-mount time so we ignore historical rows that
     slip in via INSERT replays. Refs vs state — we never want to
     re-render on this. */
  const mountTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!user?.id) return;
    mountTimeRef.current = Date.now();
    const channel = supabase
      .channel(`shell-toast:${user.id}:${Math.random().toString(36).slice(2, 8)}`)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .on("postgres_changes" as any,
        { event: "INSERT", schema: "public", table: "placement_notifications", filter: `user_id=eq.${user.id}` },
        (payload: { new: NewNotificationRow }) => {
          const row = payload?.new;
          if (!row) return;
          const created = new Date(row.created_at).getTime();
          if (Number.isNaN(created) || created < mountTimeRef.current) return;
          if (row.read_at) return;
          /* v50.185 — Monzo money treatment for money-moving kinds.
             invoice_paid, milestone_marked_met, milestone_deemed_met
             all represent £ moving from coord to partner. Direction is
             inferred from the viewer's role: partner side reads "in",
             coord side reads "out", hybrid defaults to in (partners
             are the typical recipient of the payment toast). The
             toaster doesn't know placement membership, so role is the
             best heuristic without an extra round-trip. */
          const money = moneyForKind(row.kind, row.payload || {}, user.role);
          toast({
            kind:     toastKind(row.kind),
            msg:      kindLabel(row.kind),
            subtitle: summarize(row.kind, row.payload || {}),
            onClick:  () => navigate(routeFor(row.kind, { placement_id: row.placement_id, ...(row.payload || {}) })),
            ...(money ? { money } : {}),
          });
        })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user?.id, toast, navigate]);

  return null;
}

/* v50.16 — kind-aware deep-link. Bid-engine events route to the
   specific tender; placement events route to the deal; fallback is
   /notifications which has the full feed. */
function routeFor(k: string, p: Record<string, unknown>): string {
  if (
    k === "bid_opportunity_matched"       ||
    k === "tender_amendment_published"    ||
    k === "tender_clarification_raised"   ||
    k === "tender_clarification_answered" ||
    k === "bid_pack_submitted"            ||
    k === "bid_outcome_shortlisted"       ||
    k === "bid_outcome_won"               ||
    k === "bid_outcome_lost"               ||
    k === "tender_invitation_received"     ||
    k === "tender_invitation_reminder"
  ) {
    const oppId = typeof p.opportunity_id === "string" ? p.opportunity_id : null;
    return oppId ? `/tenders/${oppId}` : "/notifications";
  }
  if (k === "message" || k === "stage_change" || k === "log_submitted" || k === "log_confirmed" || k === "rating") {
    const placementId = typeof p.placement_id === "string" ? p.placement_id : null;
    return placementId ? `/deals/${placementId}` : "/notifications";
  }
  if (k === "milestone_evidence_submitted" || k === "milestone_marked_met" || k === "milestone_deemed_met") {
    const placementId = typeof p.placement_id === "string" ? p.placement_id : null;
    return placementId ? `/deals/${placementId}?tab=milestones` : "/notifications";
  }
  if (k === "invoice_sent" || k === "invoice_paid") {
    const placementId = typeof p.placement_id === "string" ? p.placement_id : null;
    return placementId ? `/deals/${placementId}?tab=invoices` : "/notifications";
  }
  if (k === "dispute_filed" || k === "dispute_updated") return "/disputes";
  /* v50.330 — Defence P4: rating to-do deep-links straight into the
     deal with the rate modal auto-opened. */
  if (k === "rating_due") {
    const placementId = typeof p.placement_id === "string" ? p.placement_id : null;
    return placementId ? `/deals/${placementId}?rate=1` : "/notifications";
  }
  return "/notifications";
}

/* ──────────────────────────────────────────────────────────────────────── */

function toastKind(k: string): "success" | "warn" | "info" {
  switch (k) {
    case "bid_outcome_won":
    case "log_confirmed":
    case "milestone_marked_met":
    case "invoice_paid":
      return "success";
    case "bid_outcome_lost":
    case "dispute_filed":
    case "compliance_expiring":
      return "warn";
    default:
      return "info";
  }
}

function kindLabel(k: string): string {
  switch (k) {
    case "proposal_received":              return "New proposal";
    case "message":                        return "New message";
    case "stage_change":                   return "Deal stage updated";
    case "log_submitted":                  return "Daily log submitted";
    case "log_confirmed":                  return "Daily log confirmed";
    case "rating":                         return "New rating";
    case "contract_offer_sent":            return "Contract offer";
    case "contract_offer_responded":       return "Contract response";
    case "compliance_requested":           return "Compliance request";
    case "compliance_fulfilled":           return "Compliance fulfilled";
    case "compliance_expiring":            return "Cert expiring";
    case "dispute_filed":                  return "Dispute filed";
    case "dispute_updated":                return "Dispute updated";
    case "milestone_evidence_submitted":   return "Milestone evidence";
    case "milestone_marked_met":           return "Milestone met";
    case "milestone_deemed_met":           return "Milestone deemed met";
    case "invoice_sent":                   return "Invoice received";
    case "invoice_paid":                   return "Invoice paid";
    case "bid_opportunity_matched":        return "Tender match";
    case "tender_amendment_published":     return "Tender amendment";
    case "tender_clarification_raised":    return "Pre-bid Q&A";
    case "tender_clarification_answered":  return "Q&A answered";
    case "bid_pack_submitted":             return "New bid received";
    case "bid_outcome_shortlisted":        return "Bid shortlisted";
    case "bid_outcome_won":                return "Bid won";
    case "bid_outcome_lost":               return "Bid not selected";
    case "rating_due":                     return "Rate your placement";
    default:                               return "New notification";
  }
}

/* v50.185 — Pull a money amount + direction out of the payload for
   the kinds that represent £ actually moving. Returns null for
   non-money kinds. Direction follows the viewer's role since the
   notification fires per-recipient — partner = received (in), coord
   = paid (out). Hybrid defaults to "in" because the common case is a
   partner-side payment receipt. */
function moneyForKind(
  k:    string,
  p:    Record<string, unknown>,
  role: string | undefined,
): { pence: number; direction: "in" | "out" } | null {
  const isMoney =
    k === "invoice_paid" ||
    k === "milestone_marked_met" ||
    k === "milestone_deemed_met";
  if (!isMoney) return null;
  const raw = k === "invoice_paid" ? p.total_pence : p.value_pence;
  const pence = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(pence) || pence <= 0) return null;
  const direction: "in" | "out" = role === "coordinator" ? "out" : "in";
  return { pence, direction };
}

/* Short subtitle so the toast stays scannable. Full detail lives on
   /notifications. */
function summarize(k: string, p: Record<string, unknown>): string {
  switch (k) {
    case "bid_opportunity_matched":
    case "tender_amendment_published":
    case "tender_clarification_answered":
    case "bid_outcome_shortlisted":
    case "bid_outcome_won":
    case "bid_outcome_lost":
      return p.opportunity_title ? `"${String(p.opportunity_title)}"` : "Open notifications →";
    case "tender_clarification_raised":
    case "bid_pack_submitted":
      return p.opportunity_title ? `"${String(p.opportunity_title)}"` : "Open notifications →";
    case "message":
      return p.preview ? String(p.preview).slice(0, 80) : "Open the deal →";
    case "proposal_received": {
      const opName = p.operative_name ? String(p.operative_name) : "an operative";
      const trade  = p.operative_trade ? ` · ${String(p.operative_trade)}` : "";
      return `${opName}${trade}`;
    }
    case "rating_due":
      return p.counterparty_name
        ? `Placement with ${String(p.counterparty_name)} completed — takes ~90 seconds`
        : "Placement completed — takes ~90 seconds";
    default:
      return "Open notifications →";
  }
}
